import React, { useCallback, useState, useRef, useMemo } from "react";
import cn from "classnames";

import useClickOutside from "../../useCases/clickOutside";
import { DropdownSelectionOption } from "./models";

import styles from "./styles.module.scss";

interface SelectionMenuItemProps {
  iconClassName?: string;
  option: DropdownSelectionOption;
  onClick: (selected: DropdownSelectionOption) => void;
}

const SelectionMenuItem: React.FC<SelectionMenuItemProps> = props => {
  const { iconClassName, option, onClick } = props;

  const onItemClicked = useCallback(() => {
    onClick(option);
  }, [option, onClick]);

  return (
    <button className={styles.selectionMenuItem} onClick={onItemClicked}>
      <div className={styles.selectionMenuItemContent}>
        {option.icon != null && (
          <div className={iconClassName}>{option.icon}</div>
        )}
        <div className={styles.selectionMenuItemTitle}>{option.name}</div>
      </div>
    </button>
  );
};

interface DropdownSelectionProps {
  buttonClassName?: string;
  buttonIcon?: React.ReactNode;
  menuAnchorClassName?: string;
  options: DropdownSelectionOption[];
  selectedOptionValue: string | null;
  onOptionSelected: (selected: DropdownSelectionOption) => void;
  disabled?: boolean;
}

const DropdownSelection: React.FC<DropdownSelectionProps> = props => {
  const {
    buttonClassName,
    buttonIcon,
    menuAnchorClassName,
    options,
    selectedOptionValue,
    onOptionSelected,
    disabled,
  } = props;
  const [isActive, setIsActive] = useState(false);

  const selectedOption = useMemo(() => {
    return options.find(op => op.value === selectedOptionValue) ?? null;
  }, [options, selectedOptionValue]);

  const onMenuButtonClicked = useCallback(() => {
    setIsActive(!isActive);
  }, [isActive]);

  const onOptionItemClicked = useCallback(
    async (selected: DropdownSelectionOption) => {
      onOptionSelected(selected);
      setIsActive(false);
    },
    [onOptionSelected]
  );

  // NOTE: Dismiss dropdown menu when click outside
  const dropDownButtonRef = useRef<HTMLButtonElement>(null);
  const onDesktopClickOutside = useCallback(() => {
    setIsActive(false);
  }, []);
  useClickOutside(dropDownButtonRef, onDesktopClickOutside);

  return (
    <div className={styles.dropDownContainer}>
      <button
        ref={dropDownButtonRef}
        className={cn(styles.dropDownButton, buttonClassName)}
        onClick={onMenuButtonClicked}
        disabled={disabled}
      >
        {buttonIcon != null && (
          <div className={styles.dropdownButtonIcon}>{buttonIcon}</div>
        )}
        {selectedOption != null && (
          <span className={styles.message}>{selectedOption.name}</span>
        )}
      </button>
      <div className={styles.dropDownMenuAnchorWrapper}>
        <div className={cn(styles.dropDownMenuAnchor, menuAnchorClassName)}>
          {isActive && (
            <div className={styles.dropDownMenu}>
              {options.map(option => (
                <SelectionMenuItem
                  key={option.value ?? "default"}
                  option={option}
                  onClick={onOptionItemClicked}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DropdownSelection;
